import React, { useState, useEffect, useLayoutEffect } from 'react';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';
import { Button, Input, Modal, Badge, EmptyState, Spinner, Card } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const roleBadge = { SuperAdmin: 'purple', RecruiterManager: 'info', Recruiter: 'success', Developer: 'warning' };

// Same solid pill style used in Users.js so action buttons read as buttons
// regardless of theme.
function ActionPill({ color, onClick, children, title, disabled }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px',
        fontSize: 11, fontWeight: 600, lineHeight: 1.2, letterSpacing: '0.01em',
        color: '#ffffff', background: color, border: 'none', borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        boxShadow: hover && !disabled
          ? `0 4px 12px ${color}55, 0 1px 2px rgba(0,0,0,0.08)`
          : `0 1px 2px ${color}33, 0 1px 1px rgba(0,0,0,0.04)`,
        transform: hover && !disabled ? 'translateY(-1px)' : 'none',
        transition: 'transform 0.12s, box-shadow 0.15s, filter 0.15s',
        filter: hover && !disabled ? 'brightness(1.06)' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Self-service page for granted non-admin users to impersonate one of their
 * allowed targets. SuperAdmin doesn't need this page (they use the Users page)
 * but is still allowed to visit it.
 */
export default function Impersonate() {
  const { startImpersonation, impersonatedAs } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget]   = useState(null);
  const [reason, setReason]   = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAPI.listMyImpersonationGrants();
        if (!cancelled) setUsers(data.users || []);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || 'Failed to load permissions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleStart = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await startImpersonation(target.id, reason.trim() || undefined);
      toast.success(`Now acting as ${target.name}`);
      setTarget(null); setReason('');
      window.location.assign('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start impersonation');
    } finally {
      setSaving(false);
    }
  };

  useLayoutEffect(() => {
    if (target) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [target]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Impersonate</h1>
        <p className="mt-1" style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Users a SuperAdmin has authorised you to act as. Every action you take during
          impersonation is logged with both your and their identity, and they will be notified
          immediately.
        </p>
      </div>

      <Card>
        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : users.length === 0 ? (
          <EmptyState
            title="No impersonation access"
            description="No SuperAdmin has granted you permission to impersonate any user yet."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <tr>
                  {['User', 'Role', 'Action'].map(h => (
                    <th key={h} className="text-left text-xs font-semibold uppercase tracking-wider py-3 whitespace-nowrap"
                        style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td className="py-3" style={{ maxWidth: 260 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span className="truncate" style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, display: 'block' }} title={u.name}>{u.name}</span>
                        <span className="truncate" style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block' }} title={u.email}>{u.email}</span>
                      </div>
                    </td>
                    <td className="py-3 whitespace-nowrap">
                      <Badge color={roleBadge[u.role] || 'default'}>{u.role}</Badge>
                    </td>
                    <td className="py-3 whitespace-nowrap">
                      {impersonatedAs ? (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Already in a session — exit first
                        </span>
                      ) : (
                        <ActionPill color="#b45309" onClick={() => setTarget(u)} title="Impersonate this user">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M17 11l2 2 4-4" />
                          </svg>
                          Impersonate
                        </ActionPill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={!!target} onClose={() => { setTarget(null); setReason(''); }}
             title="Impersonate User" size="sm">
        <div className="space-y-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            You will act as <strong style={{ color: 'var(--text-primary)' }}>{target?.name}</strong> ({target?.role}).
            All actions are audit-logged with both identities; {target?.name?.split(' ')[0] || 'they'} will
            be notified immediately and can end the session at any time.
          </p>
          <Input
            label="Reason (optional, recorded in audit log)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Cover for vacation"
          />
          <div className="flex flex-wrap gap-3 justify-end">
            <Button variant="secondary" onClick={() => { setTarget(null); setReason(''); }}>Cancel</Button>
            <Button
              onClick={handleStart}
              loading={saving}
              loadingText="Starting…"
              style={{ backgroundColor: '#b45309', borderColor: '#b45309' }}
            >
              Start impersonation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
